import React, { useState, useEffect, useCallback } from 'react';
import {
  Folder, FileText, Image, Video, Download, ArrowLeft, ExternalLink,
  PlayCircle, X, Megaphone, Package, BookOpen, Share2, FileImage, Star,
  LucideIcon, Search, Shield, Stethoscope
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import BridgeClubLayout from '../../components/BridgeClub/BridgeClubLayout';
import { bridgeClubAPI } from '../../services/bridgeClubAPI';
import './BridgeClubResources.css';

interface ResourceFolder {
  id: number;
  name: string;
  description: string;
  slug: string;
  icon: string;
  color: string;
  resource_count: number;
  total_resource_count: number;
  subfolders?: ResourceFolder[];
  breadcrumbs?: { id: number; name: string; slug: string }[];
}

interface Resource {
  id: number;
  title: string;
  description: string;
  resource_type: string;
  file_url: string | null;
  file_name: string;
  file_size_formatted: string;
  file_extension: string;
  youtube_url: string | null;
  youtube_video_id: string | null;
  is_video: boolean;
  thumbnail_url: string | null;
  is_featured: boolean;
  download_count: number;
}

const FOLDER_ICONS: Record<string, LucideIcon> = {
  folder: Folder,
  megaphone: Megaphone,
  image: Image,
  video: Video,
  'book-open': BookOpen,
  package: Package,
  'share-2': Share2,
  'play-circle': PlayCircle,
};

const FOLDER_COLORS: Record<string, string> = {
  gray: '#6b7280',
  blue: '#3b82f6',
  green: '#22c55e',
  yellow: '#eab308',
  red: '#ef4444',
  purple: '#a855f7',
  orange: '#f97316',
};

const RESOURCE_ICONS: Record<string, LucideIcon> = {
  document: FileText,
  image: Image,
  logo: FileImage,
  flyer: Megaphone,
  social_post: Share2,
  video: Video,
  feeding_guide: BookOpen,
  product_sheet: Package,
  other: FileText,
};

const BridgeClubResources: React.FC = () => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug?: string }>();

  const [folders, setFolders] = useState<ResourceFolder[]>([]);
  const [featured, setFeatured] = useState<Resource[]>([]);
  const [currentFolder, setCurrentFolder] = useState<ResourceFolder | null>(null);
  const [subfolders, setSubfolders] = useState<ResourceFolder[]>([]);
  const [resources, setResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Video modal
  const [selectedVideo, setSelectedVideo] = useState<Resource | null>(null);

  // Load root folders
  const loadRootFolders = useCallback(async () => {
    try {
      const data = await bridgeClubAPI.getResources();
      setFolders(data.folders || []);
      setFeatured(data.featured || []);
    } catch (err) {
      console.error('Error loading resources:', err);
      setError('Failed to load resources');
    }
  }, []);

  // Load folder contents
  const loadFolderContents = useCallback(async (folderSlug: string) => {
    try {
      const data = await bridgeClubAPI.getResourceFolder(folderSlug);
      setCurrentFolder(data.folder);
      setSubfolders(data.subfolders || []);
      setResources(data.resources || []);
    } catch (err) {
      console.error('Error loading folder:', err);
      setError('Failed to load folder');
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      setError('');

      if (slug) {
        await loadFolderContents(slug);
      } else {
        await loadRootFolders();
        setCurrentFolder(null);
        setSubfolders([]);
        setResources([]);
      }

      setLoading(false);
    };
    init();
  }, [slug, loadRootFolders, loadFolderContents]);

  // Track download
  const trackDownload = async (resourceId: number) => {
    try {
      await bridgeClubAPI.trackResourceDownload(resourceId);
    } catch (err) {
      console.error('Error tracking download:', err);
    }
  };

  // Handle download
  const handleDownload = (resource: Resource) => {
    if (resource.file_url) {
      trackDownload(resource.id);
      window.open(resource.file_url, '_blank');
    }
  };

  // Get folder icon
  const getFolderIcon = (iconName: string) => {
    return FOLDER_ICONS[iconName] || Folder;
  };

  // Get folder color
  const getFolderColor = (colorName: string) => {
    return FOLDER_COLORS[colorName] || '#6b7280';
  };

  // Get resource icon
  const getResourceIcon = (type: string) => {
    return RESOURCE_ICONS[type] || FileText;
  };

  // Check if file is an image
  const isImageFile = (extension: string | null | undefined): boolean => {
    if (!extension) return false;
    const imageExtensions = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'];
    return imageExtensions.includes(extension.toLowerCase());
  };

  // Get thumbnail URL
  const getThumbnailUrl = (resource: Resource): string | null => {
    if (resource.thumbnail_url) return resource.thumbnail_url;
    if (isImageFile(resource.file_extension) && resource.file_url) {
      return resource.file_url;
    }
    return null;
  };

  // Render folder card
  const renderFolderCard = (folder: ResourceFolder) => {
    const IconComponent = getFolderIcon(folder.icon);
    const color = getFolderColor(folder.color);

    return (
      <div
        key={folder.id}
        className="bc-folder-card"
        onClick={() => navigate(`/bridge-club/resources/${folder.slug}`)}
      >
        <div className="bc-folder-icon" style={{ backgroundColor: `${color}20`, color }}>
          <IconComponent size={24} />
        </div>
        <h3>{folder.name}</h3>
        {folder.description && <p>{folder.description}</p>}
        <span className="bc-item-count">
          {folder.total_resource_count} {folder.total_resource_count === 1 ? 'item' : 'items'}
        </span>
      </div>
    );
  };

  // Render resource card
  const renderResourceCard = (resource: Resource) => {
    const IconComponent = getResourceIcon(resource.resource_type);
    const thumbnailUrl = getThumbnailUrl(resource);

    return (
      <div key={resource.id} className="bc-resource-card">
        <div
          className="bc-resource-thumbnail"
          onClick={() => {
            if (resource.is_video) {
              setSelectedVideo(resource);
            } else {
              handleDownload(resource);
            }
          }}
        >
          {resource.is_video && thumbnailUrl ? (
            <>
              <img src={thumbnailUrl} alt={resource.title} />
              <div className="bc-play-overlay">
                <PlayCircle size={40} />
              </div>
            </>
          ) : thumbnailUrl ? (
            <img src={thumbnailUrl} alt={resource.title} className="bc-resource-image-preview" />
          ) : (
            <div className="bc-resource-icon-placeholder">
              <IconComponent size={32} />
            </div>
          )}
          {isImageFile(resource.file_extension) && !resource.is_video && (
            <span className="bc-file-type-badge">{resource.file_extension?.toUpperCase()}</span>
          )}
        </div>
        <div className="bc-resource-info">
          <h4>{resource.title}</h4>
          {resource.description && (
            <p className="bc-resource-description">{resource.description}</p>
          )}
          <div className="bc-resource-meta">
            {resource.file_extension && (
              <span className="bc-file-type">{resource.file_extension}</span>
            )}
            {resource.file_size_formatted && (
              <span className="bc-file-size">{resource.file_size_formatted}</span>
            )}
            {resource.is_video && <span className="bc-video-tag">Video</span>}
          </div>
        </div>
        <div className="bc-resource-actions">
          {resource.is_video ? (
            <button
              className="bc-btn bc-btn-primary"
              onClick={() => setSelectedVideo(resource)}
            >
              <PlayCircle size={16} />
              Watch
            </button>
          ) : resource.file_url ? (
            <button
              className="bc-btn bc-btn-primary"
              onClick={() => handleDownload(resource)}
            >
              <Download size={16} />
              Download
            </button>
          ) : null}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <BridgeClubLayout>
        <div className="bc-resources">
          <div className="bc-loading-state">Loading resources...</div>
        </div>
      </BridgeClubLayout>
    );
  }

  if (error) {
    return (
      <BridgeClubLayout>
        <div className="bc-resources">
          <div className="bc-error-state">{error}</div>
        </div>
      </BridgeClubLayout>
    );
  }

  const breadcrumbs = currentFolder?.breadcrumbs || [];

  return (
    <BridgeClubLayout>
      <div className="bc-resources">
        {/* Header */}
        <div className="bc-resources-header">
          {currentFolder ? (
            <>
              <button
                className="bc-back-button"
                onClick={() => {
                  if (breadcrumbs.length > 1) {
                    navigate(`/bridge-club/resources/${breadcrumbs[breadcrumbs.length - 2].slug}`);
                  } else {
                    navigate('/bridge-club/resources');
                  }
                }}
              >
                <ArrowLeft size={16} />
                Back
              </button>
              <div className="bc-breadcrumbs">
                <span
                  className="bc-breadcrumb-link"
                  onClick={() => navigate('/bridge-club/resources')}
                >
                  Resources
                </span>
                {breadcrumbs.map((crumb, index) => (
                  <React.Fragment key={crumb.id}>
                    <span className="bc-breadcrumb-separator">/</span>
                    {index === breadcrumbs.length - 1 ? (
                      <span className="bc-breadcrumb-current">{crumb.name}</span>
                    ) : (
                      <span
                        className="bc-breadcrumb-link"
                        onClick={() => navigate(`/bridge-club/resources/${crumb.slug}`)}
                      >
                        {crumb.name}
                      </span>
                    )}
                  </React.Fragment>
                ))}
              </div>
              <h1>{currentFolder.name}</h1>
              {currentFolder.description && <p>{currentFolder.description}</p>}
            </>
          ) : (
            <>
              <button
                className="bc-back-button"
                onClick={() => navigate('/bridge-club')}
              >
                <ArrowLeft size={16} />
                Dashboard
              </button>
              <h1>Resources</h1>
              <p>Videos, guides, and helpful materials</p>
            </>
          )}
        </div>

        {/* Farm Tools Section (only on root) */}
        {!currentFolder && (
          <section className="bc-tools-section">
            <h2>
              <Search size={18} />
              Farm Tools
            </h2>
            <div className="bc-tools-grid">
              <div
                className="bc-tool-card"
                onClick={() => navigate('/bridge-club/tools/health-assistant')}
              >
                <div className="bc-tool-icon" style={{ backgroundColor: '#dcfce720', color: '#16a34a' }}>
                  <Stethoscope size={28} />
                </div>
                <div className="bc-tool-info">
                  <h3>Flock Health Assistant</h3>
                  <p>Check symptoms, analyze droppings, and get weather-aware health recommendations</p>
                </div>
              </div>
              <div
                className="bc-tool-card"
                onClick={() => navigate('/bridge-club/tools/predator-id')}
              >
                <div className="bc-tool-icon" style={{ backgroundColor: '#fef2f220', color: '#dc2626' }}>
                  <Shield size={28} />
                </div>
                <div className="bc-tool-info">
                  <h3>What Killed My Animal?</h3>
                  <p>Upload a photo or answer questions to identify predators and learn prevention strategies</p>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Featured Section (only on root) */}
        {!currentFolder && featured.length > 0 && (
          <section className="bc-featured-section">
            <h2>
              <Star size={18} />
              Featured
            </h2>
            <div className="bc-featured-scroll">
              {featured.map(resource => {
                const thumbUrl = getThumbnailUrl(resource);
                return (
                  <div
                    key={resource.id}
                    className="bc-featured-card"
                    onClick={() => {
                      if (resource.is_video) {
                        setSelectedVideo(resource);
                      } else {
                        handleDownload(resource);
                      }
                    }}
                  >
                    <div className="bc-featured-thumbnail">
                      {thumbUrl ? (
                        <>
                          <img src={thumbUrl} alt={resource.title} />
                          {resource.is_video && (
                            <div className="bc-play-overlay">
                              <PlayCircle size={32} />
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="bc-resource-icon-placeholder">
                          {React.createElement(getResourceIcon(resource.resource_type), { size: 28 })}
                        </div>
                      )}
                    </div>
                    <div className="bc-featured-info">
                      <h4>{resource.title}</h4>
                      <span className="bc-featured-type">
                        {resource.is_video ? 'Video' : resource.file_extension || 'File'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Folders Section */}
        {(currentFolder ? subfolders : folders).length > 0 && (
          <section className="bc-folders-section">
            <h2>{currentFolder ? 'Subfolders' : 'Folders'}</h2>
            <div className="bc-folders-grid">
              {(currentFolder ? subfolders : folders).map(renderFolderCard)}
            </div>
          </section>
        )}

        {/* Resources Section */}
        {currentFolder && resources.length > 0 && (
          <section className="bc-resources-section">
            <h2>Files</h2>
            <div className="bc-resources-grid">
              {resources.map(renderResourceCard)}
            </div>
          </section>
        )}

        {/* Empty State */}
        {!loading && !currentFolder && folders.length === 0 && featured.length === 0 && (
          <div className="bc-empty-state">
            <Folder size={48} />
            <h3>No resources yet</h3>
            <p>Check back later for helpful videos and guides.</p>
          </div>
        )}

        {/* Video Modal */}
        {selectedVideo && selectedVideo.youtube_video_id && (
          <div className="bc-video-modal-overlay" onClick={() => setSelectedVideo(null)}>
            <div className="bc-video-modal" onClick={(e) => e.stopPropagation()}>
              <button className="bc-video-close" onClick={() => setSelectedVideo(null)}>
                <X size={24} />
              </button>
              <h3>{selectedVideo.title}</h3>
              <div className="bc-video-container">
                <iframe
                  src={`https://www.youtube.com/embed/${selectedVideo.youtube_video_id}?autoplay=1`}
                  title={selectedVideo.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </BridgeClubLayout>
  );
};

export default BridgeClubResources;
